/* SubscribersTab — Studio admin view of all Continuity subscribers.
   Reads GET /api/subscriptions/admin/list. Surfaces status + tier + next
   renewal so Kaily can spot churn-risk customers (past_due / cancel-at-end)
   before they fully lapse. */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCcw, Mail, ExternalLink, Send, Clock, Trash2 } from "lucide-react";
import { studioHeaders } from "@/lib/studioAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
    active: "bg-cafe-olive/15 text-cafe-olive border-cafe-olive/30",
    trialing: "bg-blue-50 text-blue-700 border-blue-200",
    past_due: "bg-amber-50 text-amber-800 border-amber-300",
    canceled: "bg-cafe-cream text-cafe-mocha border-border",
    incomplete: "bg-rose-50 text-rose-700 border-rose-200",
    unpaid: "bg-rose-50 text-rose-700 border-rose-200",
};

const TIER_LABEL = {
    "continuity-68": "Lite · RM 68",
    "continuity-168": "Standard · RM 168",
    "continuity-348": "Pro · RM 348",
};

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-MY", {
            year: "numeric", month: "short", day: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function SubscribersTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("all"); // all | active | risk | canceled

    const load = async () => {
        try {
            setLoading(true);
            const r = await axios.get(`${API}/subscriptions/admin/list`, { headers: studioHeaders() });
            setRows(r.data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load subscribers");
        } finally {
            setLoading(false);
        }
    };

    const syncFromStripe = async () => {
        if (!window.confirm("Sync all subscriptions from Stripe? This pulls every Stripe sub (including ones missed by webhooks) into the DB. Safe to re-run.")) return;
        try {
            setLoading(true);
            toast.info("Scanning Stripe — this takes 5-15s…");
            const r = await axios.post(`${API}/subscriptions/admin/sync-from-stripe`, {}, { headers: studioHeaders(), timeout: 60000 });
            const { synced, skipped, failures } = r.data || {};
            toast.success(`Synced ${synced} subscriptions${skipped ? ` · ${skipped} skipped (missing tier/email)` : ""}`);
            if (failures?.length) console.warn("[sync] failures:", failures);
            await load();
            await loadReconcileStatus();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sync failed");
        } finally {
            setLoading(false);
        }
    };

    const [reconcile, setReconcile] = useState(null);
    const loadReconcileStatus = async () => {
        try {
            const r = await axios.get(`${API}/subscriptions/admin/reconcile-status`, { headers: studioHeaders() });
            setReconcile(r.data || null);
        } catch { /* non-fatal */ }
    };

    const cleanOrphans = async () => {
        if (!window.confirm(
            "Delete subscription rows whose Stripe subscription no longer exists?\n\n" +
            "Use this after swapping Stripe keys. Rows that still exist in your current Stripe account are kept; orphans (from old keys / test data) are removed."
        )) return;
        try {
            setLoading(true);
            toast.info("Validating each subscription against Stripe…");
            const r = await axios.post(`${API}/subscriptions/admin/clean-orphans`, {}, { headers: studioHeaders(), timeout: 120000 });
            const { deleted, kept, errors } = r.data || {};
            toast.success(`Deleted ${deleted} orphan subscription${deleted !== 1 ? "s" : ""} · ${kept} kept`);
            if (errors?.length) console.warn("[clean-orphans] errors:", errors);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Cleanup failed");
        } finally { setLoading(false); }
    };


    const runMigrationCampaign = async (dryRun) => {
        const word = dryRun ? "preview" : "send";
        if (!window.confirm(
            `${dryRun ? "Preview" : "Send password setup emails"} to every paid customer who hasn't set a password yet?\n\n` +
            (dryRun
                ? "Dry run — no emails will go out. We'll just show you the count + first 20 emails."
                : "This will actually send emails via Resend. Each email contains a unique 30-day setup link.")
        )) return;
        try {
            setLoading(true);
            toast.info(dryRun ? "Scanning DB…" : "Sending — this can take 30-90s depending on volume…");
            const r = await axios.post(
                `${API}/auth/admin/migration-campaign`,
                { origin_url: window.location.origin, dry_run: !!dryRun, limit: 500 },
                { headers: studioHeaders(), timeout: 180000 },
            );
            if (dryRun) {
                const { candidates_total, already_have_password, eligible_count, eligible_preview } = r.data;
                toast.success(
                    `Dry run: ${eligible_count} would receive an email (out of ${candidates_total} paid customers; ${already_have_password} already have a password).`
                );
                if (eligible_preview?.length) console.info("[migration] first 20 eligible:", eligible_preview);
            } else {
                const { sent, failed, eligible_count } = r.data;
                toast.success(`Sent ${sent}/${eligible_count} migration emails${failed?.length ? ` · ${failed.length} failed` : ""}`);
                if (failed?.length) console.warn("[migration] failed for:", failed);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Campaign failed");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); loadReconcileStatus(); }, []);

    const stats = useMemo(() => {
        const live = rows.filter((r) => ["active", "trialing", "past_due"].includes(r.status));
        const mrr = live.reduce((sum, r) => {
            const price = { "continuity-68": 68, "continuity-168": 168, "continuity-348": 348 }[r.tier] || 0;
            return sum + price;
        }, 0);
        return {
            total: rows.length,
            active: live.length,
            past_due: rows.filter((r) => r.status === "past_due").length,
            ending: rows.filter((r) => r.cancel_at_period_end && r.status === "active").length,
            mrr,
        };
    }, [rows]);

    const visibleRows = useMemo(() => {
        switch (filter) {
            case "active":
                return rows.filter((r) => ["active", "trialing"].includes(r.status));
            case "risk":
                return rows.filter((r) => r.status === "past_due" || r.cancel_at_period_end);
            case "canceled":
                return rows.filter((r) => r.status === "canceled");
            default:
                return rows;
        }
    }, [rows, filter]);

    return (
        <div className="space-y-6" data-testid="subscribers-tab">
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard label="Active subs" value={stats.active} testid="kpi-active" />
                <KpiCard label="MRR (RM)" value={stats.mrr.toLocaleString()} testid="kpi-mrr" accent />
                <KpiCard label="Past due" value={stats.past_due} testid="kpi-past-due" warn={stats.past_due > 0} />
                <KpiCard label="Ending at period close" value={stats.ending} testid="kpi-ending" warn={stats.ending > 0} />
            </div>

            {/* Toolbar */}
            <div className="flex items-center justify-between gap-3 border-b border-border pb-3">
                <div className="flex items-center gap-2 text-xs">
                    {[
                        { id: "all", label: `All (${rows.length})` },
                        { id: "active", label: `Active (${stats.active})` },
                        { id: "risk", label: `At risk (${stats.past_due + stats.ending})` },
                        { id: "canceled", label: `Canceled` },
                    ].map((f) => (
                        <button
                            key={f.id}
                            onClick={() => setFilter(f.id)}
                            className={`px-3 py-1.5 rounded-full font-bold uppercase tracking-wider ${filter === f.id ? "bg-cafe-olive text-cafe-cream" : "text-cafe-mocha hover:bg-cafe-cream"}`}
                            data-testid={`filter-${f.id}`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="text-xs font-bold uppercase tracking-wider text-cafe-mocha hover:text-cafe-olive flex items-center gap-1.5"
                    data-testid="refresh-subs"
                >
                    <RefreshCcw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
                <button
                    onClick={syncFromStripe}
                    disabled={loading}
                    className="text-xs font-bold uppercase tracking-wider text-cafe-terracotta hover:text-cafe-terracotta/80 flex items-center gap-1.5 ml-3"
                    data-testid="sync-from-stripe"
                    title="Pull all subscriptions from Stripe — use this if a webhook failed"
                >
                    ⟲ Sync from Stripe
                </button>
                <button
                    onClick={cleanOrphans}
                    disabled={loading}
                    className="text-xs font-bold uppercase tracking-wider text-cafe-terracotta hover:text-cafe-terracotta/80 flex items-center gap-1.5 ml-3"
                    data-testid="clean-orphans-btn"
                    title="Delete subscription rows whose Stripe sub no longer exists (after key swap or test data cleanup)"
                >
                    <Trash2 className="h-3 w-3" /> Clean orphans
                </button>
                <button
                    onClick={() => runMigrationCampaign(true)}
                    disabled={loading}
                    className="text-xs font-bold uppercase tracking-wider text-cafe-mocha hover:text-cafe-olive flex items-center gap-1.5 ml-3"
                    data-testid="migration-dry-run-btn"
                    title="Preview: how many customers would get an email"
                >
                    <Send className="h-3 w-3" /> Migration · Preview
                </button>
                <button
                    onClick={() => runMigrationCampaign(false)}
                    disabled={loading}
                    className="text-xs font-bold uppercase tracking-wider bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream rounded-full px-3 py-1.5 flex items-center gap-1.5"
                    data-testid="migration-send-btn"
                    title="Email all paid customers who haven't set a password yet"
                >
                    <Send className="h-3 w-3" /> Send setup emails
                </button>
            </div>

            {/* Reconcile status banner */}
            {reconcile && reconcile.last_run_at && (
                <div className="flex items-center gap-2 text-[11px] text-cafe-mocha bg-cafe-cream/50 rounded-lg px-3 py-2 -mt-2" data-testid="reconcile-status">
                    <Clock className="h-3 w-3" />
                    <span>
                        Daily Stripe reconcile · last run{" "}
                        {(() => {
                            try {
                                const diff = Date.now() - new Date(reconcile.last_run_at).getTime();
                                const h = Math.floor(diff / 3_600_000);
                                if (h < 1) return "less than an hour ago";
                                if (h < 24) return `${h} hour${h > 1 ? "s" : ""} ago`;
                                return `${Math.floor(h / 24)} day${Math.floor(h / 24) > 1 ? "s" : ""} ago`;
                            } catch { return "—"; }
                        })()}
                        {" · "}synced {reconcile.synced ?? 0} · skipped {reconcile.skipped ?? 0}
                        {(reconcile.failed ?? 0) > 0 && <> · <span className="text-cafe-terracotta">failed {reconcile.failed}</span></>}
                    </span>
                </div>
            )}

            {/* Table */}
            {visibleRows.length === 0 ? (
                <div className="text-center py-16 text-cafe-mocha italic">
                    {loading ? "Loading…" : "No subscribers in this filter yet."}
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.18em] text-cafe-mocha border-b border-border">
                            <tr>
                                <th className="text-left py-2 pr-4 font-bold">Email</th>
                                <th className="text-left py-2 pr-4 font-bold">Tier</th>
                                <th className="text-left py-2 pr-4 font-bold">Status</th>
                                <th className="text-left py-2 pr-4 font-bold">Next renewal</th>
                                <th className="text-left py-2 pr-4 font-bold">Updated</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.map((r) => (
                                <tr key={r.stripe_subscription_id || r.email} className="border-b border-border/40 hover:bg-cafe-cream/40" data-testid={`subs-row-${r.email}`}>
                                    <td className="py-3 pr-4">
                                        <a href={`mailto:${r.email}`} className="text-cafe-espresso hover:text-cafe-olive font-medium flex items-center gap-1.5">
                                            <Mail className="h-3 w-3 opacity-60" /> {r.email}
                                        </a>
                                    </td>
                                    <td className="py-3 pr-4 text-cafe-mocha font-mono text-xs">{TIER_LABEL[r.tier] || r.tier}</td>
                                    <td className="py-3 pr-4">
                                        <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-bold ${STATUS_STYLES[r.status] || STATUS_STYLES.canceled}`}>
                                            {r.status}
                                        </span>
                                        {r.cancel_at_period_end && r.status === "active" && (
                                            <span className="ml-2 text-[10px] text-cafe-terracotta uppercase tracking-wider">ending</span>
                                        )}
                                    </td>
                                    <td className="py-3 pr-4 text-cafe-mocha">{formatDate(r.current_period_end)}</td>
                                    <td className="py-3 pr-4 text-cafe-mocha text-xs">{formatDate(r.updated_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <p className="text-[11px] text-cafe-mocha italic pt-3 border-t border-border">
                Source of truth: Stripe webhooks → `subscriptions` collection. Cancel / refund flows live in the Stripe Dashboard (<a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-cafe-olive inline-flex items-center gap-0.5">open <ExternalLink className="h-3 w-3" /></a>).
            </p>
        </div>
    );
}

function KpiCard({ label, value, testid, warn, accent }) {
    return (
        <div
            className={`bg-white border rounded-2xl p-4 ${accent ? "border-cafe-olive/40 bg-cafe-olive/5" : warn ? "border-cafe-terracotta/40 bg-cafe-terracotta/5" : "border-border"}`}
            data-testid={testid}
        >
            <p className="text-[10px] uppercase tracking-[0.18em] text-cafe-mocha font-bold mb-1">{label}</p>
            <p className={`font-serif text-3xl ${accent ? "text-cafe-olive" : warn ? "text-cafe-terracotta" : "text-cafe-espresso"}`}>{value}</p>
        </div>
    );
}
